import { Type } from '@angular/core';

import { ProjectSearchComponent } from '../../project_search/project-search.component';
import { ProjectActivityChartComponent } from '../../../core/modules/project/projectActivityChart/project.activity.chart.component';
import { ProjectActivityTimelineComponent } from '../../project-activity-timeline/project.activity.timeline.component';
import { ProjectTrackersComponent } from '../../../core/modules/project/projectTrackers/project.trackers.component';
import { ProjectBuildsComponent } from '../../../core/modules/project/projectBuilds/project.builds.component';
import { ProjectSprintBurndownComponent } from '../../../core/modules/project/projectSprints/projectSprintBurndown/project.sprint.burndown.component';
import { TrackerQueryEmbeddableComponent } from '../trackerQueryEmbeddable/tracker.query.embeddable.component';

export interface MacroContext {
	[key: string]: any;
	project?: any;
	details?: any;
	availableModules?: { [key: string]: boolean };
}

export interface MacroDefinition {
	token: string;
	placeholderId: string;
	component: Type<any>;
	resolveInputs?: (context: MacroContext) => { [key: string]: any } | undefined;
	shouldRender?: (context: MacroContext) => boolean;
	parameterized?: boolean;
}

export const MACRO_REGISTRY: { [token: string]: MacroDefinition } = {
	PROJECT_SEARCH: {
		token: 'PROJECT_SEARCH',
		placeholderId: 'project-search',
		component: ProjectSearchComponent
	},
	PROJECT_ACTIVITY_CHART: {
		token: 'PROJECT_ACTIVITY_CHART',
		placeholderId: 'project-activity-chart',
		component: ProjectActivityChartComponent,
		resolveInputs: (context: MacroContext) => ({ project: context && context.project })
	},
	PROJECT_ACTIVITY_SUMMARY: {
		token: 'PROJECT_ACTIVITY_SUMMARY',
		placeholderId: 'project-activity-timeline',
		component: ProjectActivityTimelineComponent,
		resolveInputs: (context: MacroContext) => {
			const details = context && context.details;
			const project = context && context.project;
			const activityFromDetails = details && details.rel && details.rel.activity_log;
			const activityFromProject = project && project.rel && project.rel.activity_log;
			return {
				items: activityFromDetails || activityFromProject || [],
			showUser: true
			};
		}
	},
	PROJECT_TRACKERS: {
		token: 'PROJECT_TRACKERS',
		placeholderId: 'project-trackers',
		component: ProjectTrackersComponent,
		resolveInputs: (context: MacroContext) => ({ project: context && context.project }),
		shouldRender: (context: MacroContext) => {
			const modules = context && context.availableModules;
			if (!modules || typeof modules.tracker === 'undefined') {
				return true;
			}
			return !!modules.tracker;
		}
	},
	BURNDOWN: {
		token: 'BURNDOWN',
		placeholderId: 'project-burndown',
		component: ProjectSprintBurndownComponent,
		resolveInputs: (context: MacroContext) => {
			const project = context?.project ?? context?.details;
			if (!project) {
				return undefined;
			}

			const inputs: { [key: string]: any } = { project };

			const detailCandidate = context?.details;
			if (detailCandidate?.rel?.burndown) {
				inputs.sprint = detailCandidate;
				inputs.burndown = detailCandidate.rel.burndown;
			}

			const sprintCandidates = [context?.sprintId, detailCandidate?.activeSprintId];
			for (const candidate of sprintCandidates) {
				const parsed = typeof candidate === 'string' ? parseInt(candidate, 10) : candidate;
				if (typeof parsed === 'number' && !isNaN(parsed)) {
					inputs.sprintId = parsed;
					break;
				}
			}

			return inputs;
		},
		shouldRender: (context: MacroContext) => {
			const modules = context && context.availableModules;
			if (!modules || typeof modules.tracker === 'undefined') {
				return true;
			}
			return !!modules.tracker;
		}
	},
	PROJECT_BUILDS: {
		token: 'PROJECT_BUILDS',
		placeholderId: 'project-builds',
		component: ProjectBuildsComponent,
		shouldRender: (context: MacroContext) => {
			const modules = context && context.availableModules;
			if (!modules || typeof modules.build === 'undefined') {
				return true;
			}
			return !!modules.build;
		}
	},
	TRACKER_QUERY_RESULTS: {
		token: 'TRACKER_QUERY_RESULTS',
		placeholderId: 'tracker-query-results',
		component: TrackerQueryEmbeddableComponent,
		parameterized: true,
		resolveInputs: (context: MacroContext) => {
			return { url: context.macroParam || '' };
		}
	}
};

